import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import WalletToWallet from './transfers/WalletToWallet';
import InterOperatorTransfer from './transfers/InterOperatorTransfer';
import CardToMobile from './transfers/CardToMobile';
import TransferHistory from './transfers/TransferHistory';
import FundTransferRequest from './transfers/FundTransferRequest';
import { Send } from 'lucide-react';
import { useLocalization } from '../contexts/LocalizationContext';

const VALID_TABS = ['wallet-to-wallet', 'inter-operator', 'card-to-mobile', 'wallet-to-bank', 'bank-to-wallet', 'history'] as const;

const Transfers: React.FC = () => {
  const { t } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sidebar sub-items drive the tab via ?tab=... Default to the first type
  // when the URL carries no tab (preserves the pre-submenu bookmark behavior).
  const urlTab = searchParams.get('tab');
  const initialTab = urlTab && (VALID_TABS as readonly string[]).includes(urlTab)
    ? urlTab
    : 'wallet-to-wallet';
  const [activeTab, setActiveTabState] = useState(initialTab);

  // Keep activeTab in sync when the URL changes (e.g. the user clicks a
  // sidebar sub-item while this page is already mounted — no re-mount,
  // just a search string change).
  useEffect(() => {
    if (urlTab && (VALID_TABS as readonly string[]).includes(urlTab) && urlTab !== activeTab) {
      setActiveTabState(urlTab);
    }
  }, [urlTab, activeTab]);

  // Tabs are gone from the page body — sidebar sub-items drive the content.
  // Keeping setSearchParams reserved for any future deep-link programmatic
  // navigation inside sub-pages (e.g. history row click → wallet-to-bank).
  void setSearchParams;

  const renderContent = () => {
    switch (activeTab) {
      case 'wallet-to-wallet':
        return <WalletToWallet />;
      case 'inter-operator':
        return <InterOperatorTransfer />;
      case 'card-to-mobile':
        return <CardToMobile />;
      case 'wallet-to-bank':
        return <FundTransferRequest direction="withdraw" />;
      case 'bank-to-wallet':
        return <FundTransferRequest direction="deposit" />;
      case 'history':
        return <TransferHistory onlyBankTransfers />;
      default:
        return <WalletToWallet />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header amélioré */}
<div className="bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-blue-500/10 rounded-2xl p-6 lg:p-7 text-slate-800 shadow-lg border border-indigo-200/50 backdrop-blur-sm">
  <div className="flex items-center justify-between">
    <div className="flex items-center space-x-4">
      <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm">
        <Send className="h-5 w-5 lg:h-6 lg:w-6 text-indigo-600" />
      </div>
      <div>
        <h1 className="text-xl lg:text-2xl font-bold mb-1 text-slate-800">{t('transfers.title')}</h1>
        <p className="text-slate-600 text-xs lg:text-sm">
          {t('transfers.subtitle')}
        </p>
      </div>
    </div>
    <div className="hidden lg:flex items-center space-x-2 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
      <span className="text-xs font-medium text-emerald-700">{t('common.liveSystem')}</span>
    </div>
  </div>
  
  {/* Quick Stats - Version raffinée */}
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
    <div className="bg-white rounded-lg p-3 border border-slate-200 hover:border-indigo-300 transition-all duration-200 shadow-sm">
      <p className="text-slate-500 text-xs font-medium">{t('transfers.availableTypes')}</p>
      <p className="text-slate-900 font-semibold text-base">3</p>
    </div>
    {/* <div className="bg-white rounded-lg p-3 border border-slate-200 hover:border-indigo-300 transition-all duration-200 shadow-sm">
      <p className="text-slate-500 text-xs font-medium">Security</p>
      <p className="text-slate-900 font-semibold text-base">PIN Protected</p>
    </div> */}
    <div className="bg-white rounded-lg p-3 border border-slate-200 hover:border-indigo-300 transition-all duration-200 shadow-sm">
      <p className="text-slate-500 text-xs font-medium">{t('transfers.realtime')}</p>
      <p className="text-slate-900 font-semibold text-base">{t('transfers.liveTracking')}</p>
    </div>
    <div className="bg-white rounded-lg p-3 border border-slate-200 hover:border-indigo-300 transition-all duration-200 shadow-sm">
      <p className="text-slate-500 text-xs font-medium">{t('transfers.bankTransfers')}</p>
      <p className="text-slate-900 font-semibold text-base">{t('common.comingSoon')}</p>
    </div>
  </div>
</div>

      {/* Content — tab is driven by the sidebar sub-item (?tab=...) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 lg:p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Transfers;